import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './UnitInfo.css';
import MoveInDate from '../MoveInDate/MoveInDate';

interface UnitInfo {
  unit_number: string;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  floor: number;
  floor_plan: string;
  first_available_date: string;
}

const UnitInfo: React.FC = () => {
  const { propertyId, unitNumber } = useParams<{ propertyId: string; unitNumber: string }>();
  const [unitInfo, setUnitInfo] = useState<UnitInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUnitInfo = async () => {
      try {
        // Get propertyId and unitNumber from either URL params or localStorage
        const effectivePropertyId = propertyId || localStorage.getItem('selectedPropertyId');
        const effectiveUnitNumber = unitNumber || localStorage.getItem('selectedUnitNumber');

        console.log('Fetching unit info with:', { effectivePropertyId, effectiveUnitNumber });

        if (!effectivePropertyId || !effectiveUnitNumber) {
          throw new Error('Missing property or unit information');
        }

        const response = await fetch(
          `https://${process.env.REACT_APP_BASE_URL}/getUnitDetails/${effectivePropertyId}/${effectiveUnitNumber}`
        );
        const data = await response.json();
        console.log('API Response:', data);
        
        // Store the unit number in localStorage for future reference
        localStorage.setItem('selectedUnitNumber', effectiveUnitNumber);
        localStorage.setItem('selectedPropertyId', effectivePropertyId);

        // The API returns an array with a single unit, so we take the first item
        const unitData = data[0];
        
        // Map the API response to our interface structure
        setUnitInfo({
          unit_number: unitData.unit_number,
          bedrooms: unitData.bedrooms,
          bathrooms: unitData.bathrooms,
          square_feet: unitData.square_feet,
          floor: parseInt(unitData.floor_number),
          floor_plan: unitData.floor_plan_id,
          first_available_date: unitData.last_updated
        });
      } catch (error) {
        console.error('Error fetching unit info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnitInfo();
  }, [propertyId, unitNumber]);

  console.log('Current state:', { isLoading, unitInfo });

  if (isLoading) {
    return <div className="loading">Loading unit information...</div>;
  }

  if (!unitInfo) {
    return <div className="error">Error loading unit information</div>;
  }

  return (
    <div className="unit-info-container">
      <div className="unit-info-grid">
        <div className="info-item">
          <span className="label">Unit Number:</span>
          <span className="value">{unitInfo.unit_number}</span>
        </div>
        <div className="info-item">
          <span className="label">Bedrooms:</span>
          <span className="value">{unitInfo.bedrooms}</span>
        </div>
        <div className="info-item">
          <span className="label">Bathrooms:</span>
          <span className="value">{unitInfo.bathrooms}</span>
        </div>
        <div className="info-item">
          <span className="label">Square Feet:</span>
          <span className="value">{unitInfo.square_feet} sq ft</span>
        </div>
        <div className="info-item">
          <span className="label">Floor:</span>
          <span className="value">{unitInfo.floor}</span>
        </div>
        <div className="info-item">
          <span className="label">Floor Plan:</span>
          <span className="value">{unitInfo.floor_plan}</span>
        </div>
        <MoveInDate />
      </div>
    </div>
  );
};

export default UnitInfo; 